import { Fragment, type FunctionComponent, useMemo } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Box, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';

import StarCircle from 'components/Icons/StarCircle';
import ForecasterProfile from 'components/ForecasterProfile';
import SubregionForecastLink from 'components/SubregionForecastLink';
import { useUserPermissionStatus } from 'selectors/user';
import type { ForecastContent } from 'types/subregion';

import styles from './SubregionForecastContentGrouped.module.scss';

const getDayOfWeek = (dateString: string): string =>
  format(new Date(`${dateString}T00:00:00`), 'EEE');

interface Props {
  className?: string;
  elevation?: number;
  forecastContent?: ForecastContent;
  forecastNote?: string | null;
  isLoading?: boolean;
  pageName?: string;
  spotId?: string;
}

const SubregionForecastContentGrouped: FunctionComponent<Props> = ({
  className,
  elevation,
  forecastContent,
  forecastNote,
  isLoading,
  pageName,
  spotId,
}) => {
  const { hasCoreForecastPermissions } = useUserPermissionStatus();
  const hideDaysToWatch = !hasCoreForecastPermissions;

  const nowTitle = useMemo(() => {
    if (!forecastContent) return null;
    const forecast = forecastContent.tomorrowIsLive
      ? forecastContent.tomorrow.forecast
      : forecastContent.today.forecast;
    return (
      <>
        {forecast.dayToWatch && <StarCircle className={styles.star} />}{' '}
        {forecastContent.tomorrowIsLive ? 'Tomorrow' : 'Today'}
      </>
    );
  }, [forecastContent]);

  const forecastHighlights = useMemo(() => {
    const highlights =
      forecastContent?.highlights.slice(0, 7).filter((day) => day.forecast !== null) ?? [];
    // add tomorrow to highlights if we're viewing today in the Now content area and tomorrow is a day to watch
    if (
      forecastContent?.tomorrowIsLive === false &&
      forecastContent?.tomorrow.forecast.dayToWatch
    ) {
      highlights.unshift({
        date: forecastContent.tomorrow.date,
        forecast: forecastContent.tomorrow.forecast,
      });
    }
    return highlights;
  }, [forecastContent]);

  const highlightsContent = useMemo(
    () =>
      forecastHighlights.map((day) => {
        return (
          <Fragment key={`day-${day.date}`}>
            <Grid item mobile={2} desktop={1}>
              <Typography component="h3" variant="callout1">
                {getDayOfWeek(day.date)}:
              </Typography>
            </Grid>
            <Grid item mobile={10} desktop={11}>
              <Typography component="h4" variant="body1" className={styles.cardText}>
                {day.forecast?.headline}
              </Typography>
            </Grid>
          </Fragment>
        );
      }),
    [forecastHighlights],
  );

  if (isLoading)
    return (
      <Skeleton
        animation="wave"
        className={styles.loadingGrouped}
        variant="rectangular"
        data-testid="subregion-forecast-content-grouped-loading"
      />
    );

  if (!forecastContent) return null;

  return (
    <Card
      className={classNames(styles.forecasterContentGrouped, className)}
      component="section"
      data-testid="subregion-forecast-content-grouped"
      elevation={elevation}
    >
      <Grid container spacing={10} padding={2}>
        <Grid item mobile={forecastHighlights.length > 0 && !hideDaysToWatch ? 6 : 12}>
          <Box
            className={
              forecastHighlights.length > 0 && !hideDaysToWatch ? styles.gridBorder : undefined
            }
          >
            <Stack component="header" direction="row" mb={2}>
              <Box sx={{ width: '100%' }}>
                <ForecasterProfile
                  conditions=""
                  iconUrl={forecastContent.forecaster.avatarUrl}
                  lastUpdate={{ timestamp: forecastContent.lastPublished }}
                  name={forecastContent.forecaster.name}
                  isShowConditionsBorder={false}
                  title={`${forecastContent.subregion.name} Forecast`}
                  isShowName
                />
                <Typography component="h3" variant="headline" mt={1}>
                  {nowTitle}
                </Typography>
              </Box>
              <Box>
                <SubregionForecastLink
                  contentBlock={forecastContent.tomorrowIsLive ? 'tomorrow' : 'today'}
                  forecasterName={forecastContent.forecaster.name}
                  pageName={pageName}
                  spotId={spotId}
                  start={forecastContent.tomorrowIsLive ? 'forecast-tomorrow' : 'forecast-today'}
                  subregionId={forecastContent.subregion.id}
                  subregionName={forecastContent.subregion.name}
                />
              </Box>
            </Stack>
            <Typography component="p" variant="subtitle1" my={2}>
              {forecastContent.tomorrowIsLive
                ? forecastContent.tomorrow.forecast.headline
                : forecastContent.today.forecast.headline}
            </Typography>
            <Typography component="p" variant="body1">
              <span
                dangerouslySetInnerHTML={{
                  __html: forecastContent.tomorrowIsLive
                    ? forecastContent.tomorrow.forecast.observation
                    : forecastContent.today.forecast.observation,
                }}
              />
            </Typography>
            {forecastNote && (
              <Typography className={styles.forecastNotes} component="div" variant="body1" mt={2}>
                {}
                <div dangerouslySetInnerHTML={{ __html: forecastNote }} />
              </Typography>
            )}
          </Box>
        </Grid>
        {forecastHighlights.length > 0 && !hideDaysToWatch && (
          <Grid item mobile={6} data-testid="subregion-forecast-content-grouped-highlights">
            <Stack component="header" direction="row" mb={2}>
              <Box sx={{ width: '100%' }}>
                <Typography component="h4" variant="headline">
                  <StarCircle className={styles.star} /> Days to Watch
                </Typography>
              </Box>
              <Box>
                <SubregionForecastLink
                  contentBlock="highlights"
                  forecasterName={forecastContent.forecaster.name}
                  pageName={pageName}
                  spotId={spotId}
                  start="forecast-highlights"
                  subregionId={forecastContent.subregion.id}
                  subregionName={forecastContent.subregion.name}
                />
              </Box>
            </Stack>
            <Grid container spacing={2}>
              {highlightsContent}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default SubregionForecastContentGrouped;
